/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import '@geoman-io/leaflet-geoman-free';
import { featureLayer } from 'esri-leaflet';
import { vectorBasemapLayer } from 'esri-leaflet-vector';
import {
  control,
  geoJSON,
  imageOverlay,
  type Layer,
  type LayersControlEventHandlerFn,
  type LeafletMouseEventHandlerFn,
  type Popup,
  type PopupEvent,
} from 'leaflet';
import { leafletLayer } from 'protomaps-leaflet';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import { useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { ArcgisToken, LandParcel, ParcelLayerProperties } from '@poweralpha/common';
import { useQuery } from '@tanstack/react-query';
import { getArcgisToken } from '../../../api';
import '../styles.css';
import { ThemeProvider, useTheme } from '@mui/material';
import { toast } from '../../../paToast';
import { createSiteBoundaryLayers, useSiteLabelLayer } from '../siteBoundaryLayers';
import type { LeafletMapProps } from '../types';
import useLegendControl from '../useLegendControl';
import useMapLayer from '../useMapLayer';
import {
  constraintsPaintRules,
  getLayerStyle,
  getLegendHtml,
  normalizeSiteId,
  projectParcelsPopup,
  projectParcelsTooltip,
  setupProjectFilterControl,
} from '../utils';

const featureLayerQuery =
  "BN_Project_Abbrev IS NOT NULL AND BN_Agreement_Status NOT IN ('Landowner Refused', 'Terminated', 'NA')";

// Define excluded values for Agreement Status and Type
const STATUS_EXCLUDED = [
  'Landowner Refused',
  'Landowner Unresponsive',
  'Expired',
  'Terminated',
] as const;
const TYPE_EXCLUDED = ['Easement'] as const;

// Define pane names and zIndex for predictable map layer order
const PANE_DEFS = [
  { name: 'projectSiteLabelPane', zIndex: 470 },
  { name: 'bnLandPane', zIndex: 460 },
  { name: 'projectSitePane', zIndex: 450 },
  { name: 'projectParcelPane', zIndex: 440 },
  { name: 'buildableAreaPane', zIndex: 430 },
  { name: 'nonBuildableConstraintPane', zIndex: 420 },
  { name: 'esrPane', zIndex: 410 },
] as const;

const setParcelPathAttributes = (layer: Layer & { feature?: { properties?: LandParcel } }) => {
  const parcel = layer.feature?.properties;
  const element = (layer as Layer & { getElement?: () => Element | undefined }).getElement?.();
  if (!parcel || !element) return;

  element.setAttribute('data-site-id', normalizeSiteId(parcel.BN_CRM_Site_ID) ?? '');
};

function LeafletMap({
  visualEsr,
  buildableAreaParcels,
  onBnLandClick,
  projectParcelsBuildable,
  nonBuildableConstraints,
  customFeatureQuery,
  selectedParcel,
  showSiteLayer,
  legendHtml,
  noLegend,
  hideBnLand,
  fitProjectParcels,
  projectParcelsFitBoundsOptions,
}: Readonly<LeafletMapProps>) {
  const map = useMap();
  const theme = useTheme();
  const scaleBar = control.scale();

  const [buildableConstraintsVisible, setBuildableConstraintsVisible] = useState(
    Boolean(nonBuildableConstraints && nonBuildableConstraints.visibility !== 'legendonly'),
  );
  const [visualEsrVisible, setVisualEsrVisible] = useState(Boolean(visualEsr));
  // State for filtering project parcels: 'all' or 'nonExcluded'
  const [projectFilter, setProjectFilter] = useState<'all' | 'nonExcluded'>('all');

  const { data: arcgisAuth } = useQuery<ArcgisToken | null>({
    queryKey: ['arcgis-token'],
    queryFn: getArcgisToken,
    select: (data) => {
      if (data && 'error' in data) {
        toast.error('Failed to fetch ArcGIS token', { toastId: 'arcgis-token-error' });
        return null;
      }
      return data;
    },
  });

  const basemapTopo = useMemo(() => {
    if (!arcgisAuth) return null;
    if (theme.palette.mode === 'light') {
      return vectorBasemapLayer('arcgis/topographic', {
        token: arcgisAuth.token,
      });
    }
    return vectorBasemapLayer('arcgis/dark-gray', {
      token: arcgisAuth.token,
    });
  }, [arcgisAuth, theme.palette.mode]);

  const basemapImagery = useMemo(() => {
    if (!arcgisAuth) return null;
    return vectorBasemapLayer('arcgis/imagery', { token: arcgisAuth.token });
  }, [arcgisAuth]);

  const bnLandLayer = useMemo(() => {
    if (!arcgisAuth || hideBnLand) return null;
    // Assign to custom pane for fixed z-order
    const layer = featureLayer({
      url: 'https://services6.arcgis.com/3iThCcnJH1q7Nzk4/arcgis/rest/services/BN_Land/FeatureServer/0',
      token: arcgisAuth.token,
      where: customFeatureQuery ?? featureLayerQuery,
      style: (feature) => {
        const parcel = feature.properties as LandParcel;
        return getLayerStyle(parcel);
      },
      // @ts-expect-error something about Esri leaflet layer
      pane: 'bnLandPane',
    });

    layer.bindTooltip(projectParcelsTooltip);
    layer.options.pmIgnore = true;
    return layer;
  }, [arcgisAuth, customFeatureQuery, hideBnLand]);

  const projectParcelLayer = useMemo(() => {
    if (!projectParcelsBuildable) return null;
    const geojson = geoJSON(projectParcelsBuildable.json, {
      pane: 'projectParcelPane',
      filter: (feat) =>
        projectFilter === 'all' ||
        (!STATUS_EXCLUDED.includes((feat.properties as LandParcel).BN_Agreement_Status) &&
          !TYPE_EXCLUDED.includes((feat.properties as LandParcel).BN_Agreement_Type)),
      style: (feature) => {
        const parcel = feature?.properties as LandParcel;
        return (
          projectParcelsBuildable.customLayerStyle?.(parcel, selectedParcel) ??
          getLayerStyle(parcel, selectedParcel)
        );
      },
    });

    if (!projectParcelsBuildable.noTooltip) {
      geojson.bindTooltip(projectParcelsBuildable.customTooltip ?? projectParcelsTooltip);
    }

    if (!projectParcelsBuildable.noPopup && !projectParcelsBuildable.customReactPopup) {
      geojson.bindPopup(
        projectParcelsBuildable.customPopup ?? projectParcelsPopup,
        projectParcelsBuildable.popupOptions,
      );
    }

    if (projectParcelsBuildable.eachLayer) {
      geojson.eachLayer(projectParcelsBuildable.eachLayer);
    }

    return geojson;
  }, [selectedParcel, projectParcelsBuildable, projectFilter]);

  useEffect(() => {
    const popupBuilder = projectParcelsBuildable?.customReactPopup;
    if (!projectParcelLayer || !popupBuilder || projectParcelsBuildable.noPopup) return;

    const rootsByPopup = new Map<Popup, Root>();
    projectParcelLayer.bindPopup((layer: Layer) => {
      const container = document.createElement('div');
      const parcel = (layer as Layer & { feature?: { properties?: LandParcel } }).feature
        ?.properties;
      if (!parcel) return container;

      // Leaflet binds one popup to the GeoJSON feature group, while the content callback
      // receives the clicked child layer. The child does not own the popup.
      const popup = projectParcelLayer.getPopup();
      if (!popup) return container;
      rootsByPopup.get(popup)?.unmount();

      const root = createRoot(container);
      flushSync(() => {
        root.render(<ThemeProvider theme={theme}>{popupBuilder(parcel)}</ThemeProvider>);
      });
      rootsByPopup.set(popup, root);
      return container;
    }, projectParcelsBuildable.popupOptions);

    const handlePopupClose = (event: PopupEvent) => {
      const root = rootsByPopup.get(event.popup);
      if (!root) return;
      root.unmount();
      rootsByPopup.delete(event.popup);
    };
    projectParcelLayer.on('popupclose', handlePopupClose);

    return () => {
      projectParcelLayer.off('popupclose', handlePopupClose);
      projectParcelLayer.unbindPopup();
      rootsByPopup.forEach((root) => {
        root.unmount();
      });
      rootsByPopup.clear();
    };
  }, [projectParcelLayer, projectParcelsBuildable, theme]);

  useEffect(() => {
    if (!fitProjectParcels || !projectParcelLayer) return;
    const bounds = projectParcelLayer.getBounds();
    if (!bounds.isValid()) return;
    map.fitBounds(bounds, projectParcelsFitBoundsOptions);
  }, [fitProjectParcels, map, projectParcelLayer, projectParcelsFitBoundsOptions]);

  const projectSiteLayers = useMemo(() => {
    if (!projectParcelsBuildable || !showSiteLayer) return null;
    return createSiteBoundaryLayers({
      json: projectParcelsBuildable.json,
      style: projectParcelsBuildable.customSiteLayerStyle,
      label: projectParcelsBuildable.customSiteLabel,
    });
  }, [projectParcelsBuildable, showSiteLayer]);
  const projectSiteLayerOutline = projectSiteLayers?.boundaries ?? null;
  useSiteLabelLayer(
    map,
    projectSiteLayers?.labels,
    Boolean(projectSiteLayerOutline),
    projectParcelsBuildable?.siteLabelMinZoom,
  );

  const esrLayer = useMemo(() => {
    if (!visualEsr) return null;
    return imageOverlay(visualEsr.url, [visualEsr.bounds.x, visualEsr.bounds.y], {
      attribution: '&copy; BrightNight Confidential',
      pane: 'esrPane',
    });
  }, [visualEsr]);

  const buildableAreaLayer = useMemo(() => {
    if (!buildableAreaParcels) return null;
    return geoJSON(buildableAreaParcels, {
      pane: 'buildableAreaPane',
      style: { color: '#00DADA', weight: 2 },
    });
  }, [buildableAreaParcels]);

  const nonBuildableConstraintLayer = useMemo(() => {
    if (!nonBuildableConstraints || nonBuildableConstraints.visibility === false) return null;
    return leafletLayer({
      url: nonBuildableConstraints.url,
      paintRules: constraintsPaintRules,
      pane: 'nonBuildableConstraintPane',
    });
  }, [nonBuildableConstraints]);

  useEffect(() => {
    if (!bnLandLayer) return;
    // Ensure styles are applied after the layer is loaded and whenever selectedParcel changes.
    const setBnLandLayerStyle = () => {
      bnLandLayer.eachFeature((f) => {
        const parcel = f.feature.properties as LandParcel;
        const style = getLayerStyle(parcel, selectedParcel);
        f.setStyle(style);
        setParcelPathAttributes(f);
      });
    };

    setBnLandLayerStyle();

    // Apply styles after (re)load.
    bnLandLayer.on('load', setBnLandLayerStyle);
    return () => {
      bnLandLayer.off('load', setBnLandLayerStyle);
    };
  }, [bnLandLayer, selectedParcel]);

  useEffect(() => {
    if (!bnLandLayer) return;

    if (onBnLandClick) {
      const handler: LeafletMouseEventHandlerFn = (event) => {
        onBnLandClick((event.propagatedFrom as ParcelLayerProperties).feature.properties);
      };
      bnLandLayer.on('click', handler);
      return () => {
        bnLandLayer.off('click', handler);
      };
    }

    if (onBnLandClick === false) {
      return;
    }

    bnLandLayer.bindPopup(projectParcelsPopup);
    return () => {
      bnLandLayer.unbindPopup?.();
    };
  }, [bnLandLayer, onBnLandClick]);

  // user flips “Buildability Constraints” or “Energy Scoring” in the LayersControl
  const onOverlayAdd = useCallback<LayersControlEventHandlerFn>((eventLayer) => {
    if (eventLayer.name === 'Buildability Constraints') {
      setBuildableConstraintsVisible(true);
    } else if (eventLayer.name === 'Energy Scoring') {
      setVisualEsrVisible(true);
    }
  }, []);
  const onOverlayRemove = useCallback<LayersControlEventHandlerFn>((eventLayer) => {
    if (eventLayer.name === 'Buildability Constraints') {
      setBuildableConstraintsVisible(false);
    } else if (eventLayer.name === 'Energy Scoring') {
      setVisualEsrVisible(false);
    }
  }, []);

  useEffect(() => {
    // helper that temporarily disables our overlay listeners
    const toggleSilently = (fn: () => void) => {
      map.off('overlayadd', onOverlayAdd);
      map.off('overlayremove', onOverlayRemove);
      fn();
      map.on('overlayadd', onOverlayAdd);
      map.on('overlayremove', onOverlayRemove);
    };

    // start listening to user events
    map.on('overlayadd', onOverlayAdd);
    map.on('overlayremove', onOverlayRemove);

    // whenever React state says “show or hide” the constraints layer,
    // do it silently (so we don’t loop back into setBuildableConstraintsVisible)
    if (nonBuildableConstraintLayer) {
      toggleSilently(() =>
        buildableConstraintsVisible
          ? nonBuildableConstraintLayer.addTo(map)
          : nonBuildableConstraintLayer.remove(),
      );
    }

    return () => {
      map.off('overlayadd', onOverlayAdd);
      map.off('overlayremove', onOverlayRemove);
      if (nonBuildableConstraintLayer) {
        toggleSilently(() => nonBuildableConstraintLayer.remove());
      }
    };
  }, [
    map,
    nonBuildableConstraintLayer,
    buildableConstraintsVisible,
    onOverlayAdd,
    onOverlayRemove,
  ]);

  const hasProjectFeatures = Boolean(projectParcelsBuildable);
  useEffect(() => {
    if (!bnLandLayer || noLegend) return;

    const overlays = {
      ...(bnLandLayer ? { bnLand: bnLandLayer } : {}),
      ...(esrLayer ? { 'Energy Scoring': esrLayer } : {}),
      ...(buildableAreaLayer ? { 'Buildable Area': buildableAreaLayer } : {}),
      ...(projectParcelLayer ? { 'Project Parcels': projectParcelLayer } : {}),
      ...(projectSiteLayerOutline ? { 'Site Boundaries': projectSiteLayerOutline } : {}),
      ...(nonBuildableConstraintLayer
        ? { 'Buildability Constraints': nonBuildableConstraintLayer }
        : {}),
    };

    //@ts-expect-error protomaps leafletLayer doesn't have right types?
    const layers = control.layers({ Terrain: basemapTopo, Imagery: basemapImagery }, overlays, {
      collapsed: false,
    });

    layers.addTo(map);
    scaleBar.addTo(map);

    const container = layers.getContainer();
    const cleanupFilter = setupProjectFilterControl(
      container,
      map,
      projectFilter,
      setProjectFilter,
      hasProjectFeatures,
      projectParcelLayer,
    );
    return () => {
      cleanupFilter();
      layers.remove();
      scaleBar.remove();
    };
  }, [
    esrLayer,
    scaleBar,
    map,
    buildableAreaLayer,
    bnLandLayer,
    projectParcelLayer,
    nonBuildableConstraintLayer,
    basemapTopo,
    basemapImagery,
    projectSiteLayerOutline,
    projectFilter,
    hasProjectFeatures,
    noLegend,
  ]);

  // Add or remove layers based on state
  useMapLayer(map, bnLandLayer, !hasProjectFeatures);
  useMapLayer(map, esrLayer, Boolean(esrLayer));
  useMapLayer(map, basemapTopo, Boolean(basemapTopo));
  useMapLayer(map, projectParcelLayer, Boolean(projectParcelLayer));
  useMapLayer(map, projectSiteLayerOutline, Boolean(projectSiteLayerOutline));
  useMapLayer(map, buildableAreaLayer, false);
  // Legend control
  const legendContent = useMemo(
    () =>
      getLegendHtml(
        buildableConstraintsVisible,
        visualEsrVisible ? visualEsr?.legendHtml : undefined,
        legendHtml,
      ),
    [visualEsrVisible, visualEsr, legendHtml, buildableConstraintsVisible],
  );
  useLegendControl(map, legendContent);

  // Create custom panes for overlays to enforce fixed z-order
  useLayoutEffect(() => {
    PANE_DEFS.forEach(({ name, zIndex }) => {
      map.getPane(name) ?? map.createPane(name);
      const pane = map.getPane(name);
      if (pane) pane.style.zIndex = String(zIndex);
    });
  }, [map]);

  return null;
}

export default LeafletMap;
